/**
 * Comprobación de la zona horaria y del reloj del equipo.
 *
 * Decisión de Solme del 14/09/2026 (`contrato-fechas-catalogo-cri-2026-09-14.md`,
 * «Decisiones de Solme»): cada caja decide la vigencia de precios, campañas y
 * cupones con su propia fecha. Esa fecha, y la hora de documentos, aperturas y
 * cierres, salen del reloj del equipo, así que no se configura una zona aparte:
 * sería un segundo reloj que podría no coincidir con el que fecha los
 * documentos. Lo que se hace es comparar y avisar.
 *
 *  - La zona del equipo contra la zona esperada del país (`timeZone` en
 *    constants/countries.js). Se compara el desfase respecto de UTC en ese
 *    instante, no el nombre: «America/Bogota» y «America/Guayaquil» son el
 *    mismo huso y no merecen aviso.
 *  - El reloj del equipo contra la hora de Business Central, que llega en la
 *    cabecera `Date` de cada respuesta (services/relojDelServidor.js).
 *
 * Límite conocido: en un país con más de un huso (Ecuador tiene Galápagos,
 * UTC−6) una tienda del huso minoritario recibiría el aviso. Si llega a haber
 * una, la zona esperada tendrá que venir de la tienda y no del país.
 */

const MINUTO_MS = 60 * 1000;

/** Diferencia entre el reloj del equipo y el de Business Central a partir de la cual se avisa. */
export const TOLERANCIA_DEL_RELOJ_MS = 5 * MINUTO_MS;

/** La zona IANA del equipo, o "" si el navegador no la da. */
export const zonaDelEquipo = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

/**
 * Minutos que una zona IANA va por delante de UTC en un instante: −360 en
 * Costa Rica, −300 en Ecuador.
 *
 * @returns {number|null} `null` si no hay zona o el navegador no la conoce.
 */
export const desfaseDeZona = (zona, instante = Date.now()) => {
  if (!zona) return null;

  try {
    const partes = new Intl.DateTimeFormat("en-US", {
      timeZone: zona,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(new Date(instante));

    const valor = {};
    for (const parte of partes) {
      if (parte.type !== "literal") valor[parte.type] = Number(parte.value);
    }

    const comoSiFueraUtc = Date.UTC(
      valor.year,
      valor.month - 1,
      valor.day,
      valor.hour,
      valor.minute,
      valor.second
    );

    return Math.round((comoSiFueraUtc - Math.floor(instante / 1000) * 1000) / MINUTO_MS);
  } catch {
    return null;
  }
};

/** «UTC−6», «UTC+5:30». */
export const escribirDesfase = (minutos) => {
  const signo = minutos < 0 ? "−" : "+";
  const absoluto = Math.abs(minutos);
  const horas = Math.floor(absoluto / 60);
  const resto = absoluto % 60;
  return `UTC${signo}${horas}${resto ? `:${String(resto).padStart(2, "0")}` : ""}`;
};

/**
 * @param {object} datos
 * @param {string} [datos.zonaEsperada]        Zona IANA del país de la caja.
 * @param {string} [datos.pais]                Nombre del país, para el texto.
 * @param {number} [datos.ahora]               Instante de la comprobación, en ms.
 * @param {number} datos.desfaseDelEquipoMin   Minutos por delante de UTC del equipo (−getTimezoneOffset()).
 * @param {string} [datos.zonaDelEquipo]       Zona IANA del equipo, para el texto.
 * @param {{servidor: number, local: number}|null} [datos.horaDelServidor]
 *   Última hora de Business Central y la hora del equipo al recibirla, en ms.
 * @param {number} [datos.tolerancia]
 * @returns {Array<{clave: "zona"|"reloj", texto: string}>} Vacío si todo cuadra o no hay con qué comparar.
 */
export const comprobarHuso = ({
  zonaEsperada,
  pais,
  ahora = Date.now(),
  desfaseDelEquipoMin,
  zonaDelEquipo: zonaEquipo = "",
  horaDelServidor = null,
  tolerancia = TOLERANCIA_DEL_RELOJ_MS,
}) => {
  const avisos = [];

  const esperado = desfaseDeZona(zonaEsperada, ahora);
  if (esperado !== null && Number.isFinite(desfaseDelEquipoMin) && esperado !== desfaseDelEquipoMin) {
    avisos.push({
      clave: "zona",
      texto:
        `La zona horaria de este equipo (${zonaEquipo || "sin nombre"}, ${escribirDesfase(desfaseDelEquipoMin)}) ` +
        `no es la de ${pais || "este país"} (${zonaEsperada}, ${escribirDesfase(esperado)}). ` +
        "La vigencia de precios, campañas y cupones y la hora de los documentos salen de este equipo: " +
        "corrija la zona horaria del sistema operativo.",
    });
  }

  if (horaDelServidor && Number.isFinite(horaDelServidor.servidor) && Number.isFinite(horaDelServidor.local)) {
    const diferencia = horaDelServidor.local - horaDelServidor.servidor;

    if (Math.abs(diferencia) > tolerancia) {
      const minutos = Math.round(Math.abs(diferencia) / MINUTO_MS);
      avisos.push({
        clave: "reloj",
        texto:
          `El reloj de este equipo va ${minutos} min ${diferencia > 0 ? "adelantado" : "atrasado"} ` +
          "respecto de Business Central. Corrija la fecha y la hora del sistema operativo.",
      });
    }
  }

  return avisos;
};
